/* eslint-disable import/no-default-export */
/**
 * Based on app/src/components/CreateBoard/PreloadHelpers
 */

import { navigate } from 'app/scripts/controller/navigate';

import _ from 'underscore';

// eslint-disable-next-line no-restricted-imports
import api from 'app/gamma/src/api';
import { unsplashClient, Photo } from '@trello/unsplash';
import { SharedState } from '@trello/shared-state';
import { BackgroundType } from 'app/src/backgrounds';
import { ProductFeatures } from '@trello/product-features';
// eslint-disable-next-line no-restricted-imports
import { loadTeam } from 'app/gamma/src/modules/loaders/load-team';
// eslint-disable-next-line no-restricted-imports
import { State } from 'app/gamma/src/modules/types';
// eslint-disable-next-line no-restricted-imports
import {
  getMyOwnedBoards,
  isWelcomeBoard,
} from 'app/gamma/src/selectors/boards';
// eslint-disable-next-line no-restricted-imports
import {
  getCreateBoardSelectedVisibility,
  getCreateBoardSelectedTeam,
} from 'app/gamma/src/selectors/create-menu';
// eslint-disable-next-line no-restricted-imports
import { getMyTeams, getTeamById } from 'app/gamma/src/selectors/teams';
import { boardsMenuState } from './boards-menu-state';
// eslint-disable-next-line no-restricted-imports
import { BoardResponse } from 'app/gamma/src/types/responses';
// eslint-disable-next-line no-restricted-imports
import {
  BoardModel,
  PermissionLevel,
  TeamModel,
} from 'app/gamma/src/types/models';
// eslint-disable-next-line no-restricted-imports
import { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
// eslint-disable-next-line no-restricted-imports
import { Action, actionCreator } from '@trello/redux';
import { getMilliseconds } from '@trello/time';
import { waitForImageLoad } from '@trello/image-previews';
import { overlayState } from './overlay';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';

// eslint-disable-next-line @trello/no-module-logic
const PRELOAD_PHOTO_EXPIRATION = getMilliseconds({ hours: 1 });
// eslint-disable-next-line @trello/no-module-logic
const PRELOAD_PERMISSION_EXPIRATION = getMilliseconds({ minutes: 1 });

export const PERSONAL_VIS_OPTIONS: PermissionLevel[] = ['private', 'public'];
export const ORG_VIS_OPTIONS: PermissionLevel[] = ['private', 'org', 'public'];
export const ENTERPRISE_VIS_OPTIONS: PermissionLevel[] = [
  'private',
  'org',
  'enterprise',
  'public',
];

export const CREATE_BOARD_SUBMIT_DONE = Symbol('CREATE_BOARD_SUBMIT_DONE');

interface TrackingOptions {
  method?: string;
}

export type CreateBoardSubmitDoneAction = Action<
  typeof CREATE_BOARD_SUBMIT_DONE,
  BoardResponse | null
>;

export interface BackgroundItemState {
  type:
    | BackgroundType.Color
    | BackgroundType.Gradient
    | BackgroundType.Unsplash
    | null;
  id: string | null;
}

export interface BackgroundState {
  selected: BackgroundItemState;
  preSelected: BackgroundItemState;
  shifted: BackgroundItemState;
}

export interface CreateMenuState {
  currentPhotosQuery: string;
  background: BackgroundState;
  name: string;
  isCreatingBoard: boolean;
  isLoadingPhotos: boolean;
  isLoadingVizPermissions: boolean;
  keepFromSource: string[];
  photos: Photo[];
  selectedTeamId: string | null;
  selectedVisibility: PermissionLevel | null;
}

export const initialState: CreateMenuState = {
  currentPhotosQuery: '',
  background: {
    preSelected: {
      type: BackgroundType.Color,
      id: 'blue',
    },
    selected: {
      type: null,
      id: null,
    },
    shifted: {
      type: null,
      id: null,
    },
  },
  name: '',
  isCreatingBoard: false,
  isLoadingPhotos: true,
  isLoadingVizPermissions: true,
  photos: [],
  selectedTeamId: null,
  selectedVisibility: null,
  keepFromSource: ['cards'],
};

export const createMenuState = new SharedState<CreateMenuState>(initialState);

// Expire memoization cache so we don't keep stale responses around too long
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const delayedExpireMemoizedCache = (fn: any, ms = 0) =>
  setTimeout(() => (fn.cache = {}), ms);

const preloadPermissions = _.memoize(
  (teams: TeamModel[], dispatch: Dispatch, getState: () => State) => {
    delayedExpireMemoizedCache(
      preloadPermissions,
      PRELOAD_PERMISSION_EXPIRATION,
    );

    return Promise.all(
      teams.map(async (org) => {
        // at this moment, we are querying all data from teams,
        // so this is hardly ever gonna be called
        if (
          !ProductFeatures.isFeatureEnabled(
            'restrictVis',
            org.products && org.products[0],
          )
        ) {
          await dispatch(loadTeam(org.id));
        }

        return Promise.resolve(getTeamById(getState(), org.id));
      }),
    );
  },
);

const preloadPhotos = _.memoize(() => {
  delayedExpireMemoizedCache(preloadPhotos, PRELOAD_PHOTO_EXPIRATION);

  return unsplashClient.getDefaultCollection().then(({ response }) =>
    // Preload photos visible on initial create board screen then resolve with the initial response
    Promise.all(
      response?.results?.map((photo) => waitForImageLoad(photo.urls.small)) ||
        [],
    ).then(() => response?.results || []),
  );
});

export const preloadCreateBoardData = (): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    createMenuState.setValue({
      ...createMenuState.value,
      isLoadingPhotos: true,
      isLoadingVizPermissions: true,
    });

    try {
      const photos = await preloadPhotos();
      const { preSelected } = createMenuState.value.background;
      if (photos?.[0]) {
        preSelected.type = BackgroundType.Unsplash;
        preSelected.id = photos[0].id;
      }
      createMenuState.setValue({
        ...createMenuState.value,
        photos,
        isLoadingPhotos: false,
        background: {
          ...createMenuState.value.background,
          preSelected,
        },
      });
    } catch (err) {
      createMenuState.setValue({
        ...createMenuState.value,
        photos: [],
        isLoadingPhotos: false,
      });
    }

    try {
      await preloadPermissions(getMyTeams(getState()), dispatch, getState);
      createMenuState.setValue({
        ...createMenuState.value,
        isLoadingVizPermissions: false,
      });
    } catch (err) {
      createMenuState.setValue({
        ...createMenuState.value,
        isLoadingVizPermissions: false,
      });
    }
  };
};

// This action is still needed because there are reducers for it elsewhere.
const createBoardSubmitDone = actionCreator<CreateBoardSubmitDoneAction>(
  CREATE_BOARD_SUBMIT_DONE,
);

export const createBoardSubmit = (
  trackingOpts?: TrackingOptions,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    createMenuState.setValue({
      ...createMenuState.value,
      isCreatingBoard: true,
    });

    const traceId = Analytics.startTask({
      taskName: 'create-board',
      source: 'appHeader',
    });

    try {
      const { name, photos } = createMenuState.value;
      const state = getState();

      const selectedTeam = getCreateBoardSelectedTeam(
        state,
        createMenuState.value,
      );
      const selectedTeamId =
        selectedTeam && selectedTeam.model && selectedTeam.model.id;

      const selectedVisibility = getCreateBoardSelectedVisibility(
        state,
        createMenuState.value,
      );

      if (!selectedVisibility) {
        throw new Error('No permission selected');
      }

      const createMenuBackground = createMenuState.value.background;
      const background = createMenuBackground.selected.id
        ? createMenuBackground.selected
        : createMenuBackground.preSelected;

      if (!background.id) {
        throw new Error('No background selected');
      }

      const ownedBoards: BoardModel[] = getMyOwnedBoards(state);
      const isFirstBoard =
        // Has no boards
        ownedBoards.length === 0 ||
        // Only has welcome board
        (ownedBoards.length === 1 &&
          isWelcomeBoard(getState(), ownedBoards[0]));
      const createDefaultLists = !!isFirstBoard;

      const requestData: {
        name: string;
        defaultLists: boolean;
        idOrganization?: string;
        prefs_background_url?: string;
        prefs_background?: string;
        prefs_permissionLevel: PermissionLevel;
        prefs_selfJoin?: boolean;
      } = {
        defaultLists: createDefaultLists,
        name,
        prefs_permissionLevel: selectedVisibility,
        prefs_selfJoin: !!selectedTeamId && selectedVisibility !== 'private',
      };

      if (selectedTeamId) {
        requestData.idOrganization = selectedTeamId;
      }

      if (background.type === BackgroundType.Unsplash) {
        const photo = photos.find(({ id }) => id === background.id);

        if (photo) {
          requestData.prefs_background_url = unsplashClient.appendImageParameters(
            photo.urls.raw,
          );
          unsplashClient.trackDownload(photo);
        }
      } else if (
        background.type === BackgroundType.Color ||
        background.type === BackgroundType.Gradient
      ) {
        requestData.prefs_background = background.id;
      }

      const createdBoard = await api
        .withTracing(traceId)
        .rest.post<BoardResponse>('boards', {
          body: requestData,
        });

      createMenuState.setValue({
        ...createMenuState.value,
        isCreatingBoard: false,
      });
      boardsMenuState.setValue({
        ...boardsMenuState.value,
        idRecentBoards: createdBoard
          ? [
              createdBoard.id,
              ...boardsMenuState.value.idRecentBoards.filter(
                (idBoard: string) => idBoard !== createdBoard.id,
              ),
            ].slice(0, 16)
          : boardsMenuState.value.idRecentBoards,
      });
      dispatch(createBoardSubmitDone(createdBoard));

      Analytics.sendCreatedBoardEvent({
        source: getScreenFromUrl(),
        containers: {
          board: {
            id: createdBoard.id,
          },
          organization: {
            id: createdBoard.idOrganization,
          },
        },
        attributes: {
          visibility: createdBoard.prefs?.permissionLevel,
          isTemplate: createdBoard.prefs?.isTemplate,
          isBCFeature: true,
          requiredBC: false,
          taskId: traceId,
          method: trackingOpts && trackingOpts.method,
        },
      });

      Analytics.taskSucceeded({
        taskName: 'create-board',
        traceId,
        source: 'appHeader',
      });

      if (createdBoard.url) {
        const params = new URLSearchParams();
        if (createDefaultLists) {
          params.append('openCardComposerInFirstList', 'true');
        } else {
          params.append('openListComposer', 'true');
        }
        const url = new URL(createdBoard.url);

        navigate(`${url.pathname}?${params.toString()}`, {
          trigger: true,
        });
      }
      overlayState.setValue({
        overlayType: null,
        context: {},
      });
    } catch (e) {
      Analytics.taskFailed({
        taskName: 'create-board',
        traceId,
        source: 'appHeader',
        error: e,
      });
      createMenuState.setValue({
        ...createMenuState.value,
        isCreatingBoard: false,
      });
      dispatch(createBoardSubmitDone());
    }
  };
};

export const createBoardFromTemplateSubmit = (
  templateId: string,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    createMenuState.setValue({
      ...createMenuState.value,
      isCreatingBoard: true,
    });

    const traceId = Analytics.startTask({
      taskName: 'create-board/template',
      source: 'useTemplateInlineDialog',
    });

    try {
      const { name, keepFromSource } = createMenuState.value;

      const selectedTeam = getCreateBoardSelectedTeam(
        getState(),
        createMenuState.value,
      );
      const selectedTeamId =
        selectedTeam && selectedTeam.model && selectedTeam.model.id;

      const selectedVisibility = getCreateBoardSelectedVisibility(
        getState(),
        createMenuState.value,
      );

      if (!selectedVisibility) {
        throw new Error('No permission selected');
      }

      const keepFromSourceRequestData = [...keepFromSource, 'templates'];

      const requestData: {
        name: string;
        idOrganization?: string;
        prefs_permissionLevel: PermissionLevel;
        prefs_isTemplate: boolean;
        idBoardSource: string;
        keepFromSource: string;
      } = {
        name,
        prefs_permissionLevel: selectedVisibility,
        prefs_isTemplate: false,
        idBoardSource: templateId,
        keepFromSource: keepFromSourceRequestData.join(),
      };

      if (selectedTeamId) {
        requestData.idOrganization = selectedTeamId;
      }
      const createdBoard = await api
        .withTracing(traceId)
        .rest.post<BoardResponse>('boards', {
          body: requestData,
        });

      createMenuState.setValue({
        ...createMenuState.value,
        isCreatingBoard: false,
      });
      dispatch(createBoardSubmitDone(createdBoard));

      Analytics.sendCreatedBoardEvent({
        source: 'useTemplateInlineDialog',
        containers: {
          board: {
            id: createdBoard.id,
          },
          organization: {
            id: createdBoard.idOrganization || '',
          },
        },
        attributes: {
          visibility: createdBoard.prefs?.permissionLevel ?? '',
          isBCFeature: true,
          requiredBC: false,
          isTemplate: true,
          taskId: traceId,
        },
      });

      Analytics.taskSucceeded({
        taskName: 'create-board/template',
        traceId,
        source: 'useTemplateInlineDialog',
      });

      if (createdBoard.url) {
        const params = new URLSearchParams();
        params.append('openListComposer', 'true');
        const url = new URL(createdBoard.url);

        navigate(`${url.pathname}?${params.toString()}`, {
          trigger: true,
        });
      }
    } catch (e) {
      Analytics.taskFailed({
        taskName: 'create-board/template',
        traceId,
        source: 'useTemplateInlineDialog',
        error: e,
      });
      createMenuState.setValue({
        ...createMenuState.value,
        isCreatingBoard: false,
      });
      dispatch(createBoardSubmitDone());
    }
  };
};
